import { useState, useEffect } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material"
import { Loader2, Search, UserPlus, Mail, Phone } from "lucide-react"
import { customersApi } from "@crm-src/lib/customers-api"
import { leadsApi } from "@crm-src/lib/leads-api"
import type { Customer } from "@crm-src/types/customer"

interface LinkProspectToResellerModalProps {
  isOpen: boolean
  onClose: () => void
  resellerId: string
  onSuccess: () => void
  excludeProspectIds?: string[] // IDs of prospects already associated with this reseller
}

export function LinkProspectToResellerModal({
  isOpen,
  onClose,
  resellerId,
  onSuccess,
  excludeProspectIds = []
}: LinkProspectToResellerModalProps) {
  const { toast } = useToast()
  const [prospects, setProspects] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedProspectIds, setSelectedProspectIds] = useState<Set<string>>(new Set())
  const [isLinking, setIsLinking] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadProspects()
      setSelectedProspectIds(new Set())
      setSearchTerm("")
    }
  }, [isOpen])

  const loadProspects = async () => {
    setLoading(true)
    try {
      // Get all leads/prospects that are not already associated with this reseller
      const response = await customersApi.getAll({
        customer_type: 'Lead',
        per_page: 1000 // Get a large number to show all available
      })

      const allProspects = response.customers || []
      // Filter out prospects already associated with this reseller
      const availableProspects = allProspects.filter(
        (prospect: Customer) =>
          !excludeProspectIds.includes(prospect.id) &&
          prospect.reseller?.id?.toString() !== resellerId
      )

      setProspects(availableProspects)
    } catch (error) {
      console.error('Error loading prospects:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load prospects"
      })
    } finally {
      setLoading(false)
    }
  }

  const handleToggleProspect = (prospectId: string) => {
    setSelectedProspectIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(prospectId)) {
        newSet.delete(prospectId)
      } else {
        newSet.add(prospectId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    const filteredProspects = getFilteredProspects()
    if (selectedProspectIds.size === filteredProspects.length) {
      setSelectedProspectIds(new Set())
    } else {
      setSelectedProspectIds(new Set(filteredProspects.map(p => p.id)))
    }
  }

  const handleLink = async () => {
    if (selectedProspectIds.size === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select at least one prospect"
      })
      return
    }

    setIsLinking(true)
    try {
      await leadsApi.bulkAssignReseller(
        Array.from(selectedProspectIds),
        resellerId
      )

      toast({
        title: "Success",
        description: `Successfully linked ${selectedProspectIds.size} prospect(s) to this reseller`
      })

      setSelectedProspectIds(new Set())
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error linking prospects:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.errors?.[0] || "Failed to link prospects"
      })
    } finally {
      setIsLinking(false)
    }
  }

  const getFilteredProspects = () => {
    if (!searchTerm) return prospects

    const term = searchTerm.toLowerCase()
    return prospects.filter(prospect =>
      prospect.company_name?.toLowerCase().includes(term) ||
      prospect.full_name?.toLowerCase().includes(term) ||
      prospect.email?.toLowerCase().includes(term)
    )
  }

  const filteredProspects = getFilteredProspects()

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { maxHeight: "80vh" } }}>
      <DialogTitle>Link Prospects to Reseller</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <DialogContentText sx={{ mb: 2 }}>
          Select existing prospects to associate with this reseller
        </DialogContentText>

        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Search */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search prospects by name, company, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />

          {/* Select All */}
          {filteredProspects.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1, borderBottom: 1, borderColor: "divider" }}>
              <Checkbox
                size="small"
                sx={{ p: 0.5 }}
                checked={selectedProspectIds.size === filteredProspects.length && filteredProspects.length > 0}
                onChange={handleSelectAll}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Select all ({filteredProspects.length} prospects)
              </Typography>
            </Box>
          )}

          {/* Prospect List */}
          <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                <Box sx={{ color: "text.disabled", display: "flex", animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }}>
                  <Loader2 size={24} />
                </Box>
              </Box>
            ) : filteredProspects.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4, color: "text.disabled" }}>
                <Typography variant="body2">
                  {searchTerm ? "No prospects found matching your search" : "No available prospects to link"}
                </Typography>
              </Box>
            ) : (
              filteredProspects.map((prospect) => (
                <Box
                  key={prospect.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  onClick={(e) => {
                    // Don't toggle if clicking directly on the checkbox (it handles its own click)
                    if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
                      return
                    }
                    handleToggleProspect(prospect.id)
                  }}
                >
                  <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex" }}>
                    <Checkbox
                      size="small"
                      sx={{ p: 0.5 }}
                      checked={selectedProspectIds.has(prospect.id)}
                      onChange={() => handleToggleProspect(prospect.id)}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ color: "warning.main", flexShrink: 0, display: "flex" }}>
                        <UserPlus size={16} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {prospect.company_name || prospect.full_name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5, fontSize: "0.75rem", color: "text.disabled" }}>
                      {prospect.email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Mail size={12} />
                          <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{prospect.email}</Box>
                        </Box>
                      )}
                      {prospect.phone && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Phone size={12} />
                          <Box component="span">{prospect.phone}</Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={isLinking}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleLink}
          disabled={isLinking || selectedProspectIds.size === 0}
          {...(isLinking ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
        >
          {isLinking
            ? "Linking..."
            : `Link ${selectedProspectIds.size > 0 ? `(${selectedProspectIds.size})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
